import {Button, Hotkey, IconButton, Link} from '@wandb/components';
import * as React from 'react';

import {urlPrefixed} from '../../../config';
import {TileMediaSpec} from '../../MediaCard';
import {useMediaBrowserModalContext} from './MediaBrowserModalContext';
import {RunSelectorDropdown} from './RunSelectorDropdown';

type MediaBrowserModalHeaderProps = {
  onClose: () => void;
  tileMediaSpec: TileMediaSpec;
  fullScreenTitle?: React.ReactNode | null;
  titleLinkToRun?: string;
  labels?: string[];
  zoomLevel?: number;
  onZoomChange?: (zoom: number) => void;
};

export const MediaBrowserModalHeader: React.FC<
  MediaBrowserModalHeaderProps
> = ({
  onClose,
  tileMediaSpec,
  fullScreenTitle,
  titleLinkToRun,
  labels,
  onZoomChange,
}) => {
  const {navigation, selectRun, navigateToPrevious, navigateToNext} =
    useMediaBrowserModalContext();

  const {hasNext, hasPrevious, currentRun, availableRuns} = navigation;

  return (
    <div className="night-aware flex w-full py-16 px-16 border-b border-moon-250">
      <div className="flex flex-1 gap-8">
        <div className="flex flex-0 items-center gap-4 pr-8">
          <IconButton
            icon="close"
            variant="secondary"
            size="md"
            onClick={onClose}
            aria-label="Close modal"
            tooltip={{
              children: (
                <>
                  <span style={{margin: '2px'}}>Close modal</span>
                  <Hotkey keys={['Esc']} name="Close modal" m={2} />
                </>
              ),
            }}
          />
        </div>

        <div className="flex flex-1 items-center justify-center gap-4 text-moon-600">
          <div className="text-lg/[32px] flex-0 h-full items-center justify-center font-semibold">
            {tileMediaSpec.mediaKey}
          </div>
          <span className="flex flex-0 h-full text-lg/[32px] items-center justify-center font-semibold">
            /
          </span>
          <div className="flex flex-1 h-full text-moon-800">
            {availableRuns && availableRuns.length > 1 && currentRun ? (
              <RunSelectorDropdown
                selectedRun={currentRun}
                onRunSelect={selectRun}
                runsWithMedia={availableRuns}
              />
            ) : fullScreenTitle && labels?.includes('run') ? (
              <span className="text-lg/[32px] text-moon-600 font-semibold">
                <Link href={urlPrefixed(titleLinkToRun)} target="_blank">
                  {fullScreenTitle}
                </Link>
              </span>
            ) : (
              <span className="text-lg/[32px] text-moon-600 font-semibold">
                {fullScreenTitle}
              </span>
            )}
          </div>
        </div>
      </div>

      {(hasNext || hasPrevious) && (
        <div className="flex flex-0 items-center justify-start">
          <div className="flex items-end justify-between gap-8">
            <Button
              aria-label="fullscreen-modal-prev-run"
              startIcon="chevron-back"
              variant="secondary"
              size="md"
              onClick={navigateToPrevious}
              isDisabled={!hasPrevious}
              tooltip={{
                children: (
                  <>
                    <span style={{margin: '2px'}}>Previous image in panel</span>
                    <Hotkey keys={['←']} name="Previous image in panel" m={2} />
                  </>
                ),
              }}>
              Previous
            </Button>
            <Button
              aria-label="fullscreen-modal-next-run"
              variant="secondary"
              size="md"
              onClick={navigateToNext}
              isDisabled={!hasNext}
              endIcon="chevron-next"
              tooltip={{
                children: (
                  <>
                    <span style={{margin: '2px'}}>Next image in panel</span>
                    <Hotkey keys={['→']} name="Next image in panel" m={2} />
                  </>
                ),
              }}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
